// Los dos últimos solo los produce el portal del cliente: una vivienda que sube
// un propietario nace `pendiente_validacion` y no se publica hasta aprobarla.
export type Estado =
  | 'disponible'
  | 'reservada'
  | 'alquilada'
  | 'vendida'
  | 'reforma'
  | 'pendiente_validacion'
  | 'rechazada';
export type Operacion = 'alquiler' | 'venta' | 'ambas';

export interface Media {
  media_id: string;
  property_id: string;
  status: 'uploading' | 'ready';
  variants?: { full: string; card: string };
}

export interface Property {
  property_id: string;
  codigo: string;
  direccion: string;
  zona: string;
  tipo: string;
  estado: Estado;
  m2: number;
  habitaciones: number;
  banos: number;
  precio_alquiler: number;
  precio_venta: number;
  operacion: Operacion;
  descripcion: string;
  destacada: boolean;
  publicada_web: 'si' | 'no';
  cover?: string | null;
  media?: Media[];
  // Rastro del portal del cliente
  owner_client_id?: string;
  origen?: string;
  motivo_rechazo?: string;
  cambios_pendientes?: Partial<Property> & { propuesto_en?: number };
}

// Los del portal quedan fuera del desplegable del formulario: no son estados
// que un agente deba poder poner a mano, se alcanzan aprobando o rechazando.
export const ESTADOS: Estado[] = ['disponible', 'reservada', 'alquilada', 'vendida', 'reforma'];
export const TIPOS = ['piso', 'atico', 'chalet', 'unifamiliar', 'estudio', 'local', 'otro'];

export const ESTADO_STYLE: Record<Estado, string> = {
  disponible: 'bg-leaf/40 text-deep',
  reservada: 'bg-amber-100 text-amber-800',
  alquilada: 'bg-sky-100 text-sky-800',
  vendida: 'bg-neutral-200 text-neutral-600',
  reforma: 'bg-orange-100 text-orange-800',
  pendiente_validacion: 'bg-amber-200 text-amber-900',
  rechazada: 'bg-red-100 text-red-800',
};

// Lo que se lee en pantalla. El valor guardado no cambia (lo usan la web, el
// importador y el portal); solo cómo se nombra, que es lo que cita la guía.
export const ESTADO_LABEL: Record<Estado, string> = {
  disponible: 'Disponible',
  reservada: 'Reservada',
  alquilada: 'Alquilada',
  vendida: 'Vendida',
  reforma: 'En reforma',
  pendiente_validacion: 'Pendiente',
  rechazada: 'Rechazada',
};

export const TIPO_LABEL: Record<string, string> = {
  piso: 'Piso',
  atico: 'Ático',
  chalet: 'Chalet',
  unifamiliar: 'Unifamiliar',
  estudio: 'Estudio',
  local: 'Local',
  otro: 'Otro',
};

export const OPERACION_LABEL: Record<Operacion, string> = {
  alquiler: 'Alquiler',
  venta: 'Venta',
  ambas: 'Alquiler y venta',
};

// Las variantes marcadas se sirven por la distribución de la web (/media/*).
// El dominio lo escribe scripts/deploy-crm.sh en .env.local desde los Outputs
// del stack activo: cero IDs de cuenta en el código.
export const MEDIA_BASE = `${import.meta.env.VITE_MEDIA_BASE ?? ''}/media`;

// ── Operaciones (C3) ──────────────────────────────────────────────
// Dos pipelines. El orden de las etapas es el del backend (ETAPAS_DEAL): el CRM
// mueve con «anterior/siguiente» y cierra en la última, así que no puede diferir.
export type TipoDeal = 'comercializacion' | 'captacion';
export type EtapaDeal =
  | 'interes' | 'visita' | 'propuesta' | 'contratado'
  | 'contacto' | 'documentacion' | 'visita_fotos' | 'alta';

// Los tres contratos que rigen desde el 19/08: cada uno cobra distinto, y por
// eso el cierre sugiere la comisión de forma diferente en cada uno.
export type Contrato = 'temporada' | 'larga_estancia' | 'venta';
export const CONTRATOS: Contrato[] = ['temporada', 'larga_estancia', 'venta'];
export const CONTRATO_LABEL: Record<Contrato, string> = {
  temporada: 'Temporada',
  larga_estancia: 'Larga estancia',
  venta: 'Venta',
};

export interface Deal {
  deal_id: string;
  tipo: TipoDeal;
  etapa: EtapaDeal;
  titulo: string;
  property_id?: string;
  client_id?: string;
  operacion?: 'venta' | 'alquiler';
  contrato?: Contrato;
  importe?: number;
  notas?: string;
  agente?: string;
  estado_cierre?: string;
  commission_id?: string;
  historial_etapas?: { etapa: EtapaDeal; ts: number }[];
  created_at?: number;
  updated_at?: string;
}

export const ETAPAS_POR_TIPO: Record<TipoDeal, EtapaDeal[]> = {
  comercializacion: ['interes', 'visita', 'propuesta', 'contratado'],
  captacion: ['contacto', 'documentacion', 'visita_fotos', 'alta'],
};

export const TIPO_DEAL_LABEL: Record<TipoDeal, string> = {
  comercializacion: 'Comercialización',
  captacion: 'Captación',
};

export const ETAPA_LABEL: Record<EtapaDeal, string> = {
  interes: 'Interés',
  visita: 'Visita',
  propuesta: 'Propuesta',
  contratado: 'Contratado',
  contacto: 'Contacto',
  documentacion: 'Documentación',
  visita_fotos: 'Visita y fotos',
  alta: 'Alta',
};

// Un degradado por columna: gris al entrar, verde marca al cerrar. La última
// etapa de cada tablero es la de «hecho», por eso comparte el verde intenso.
export const ETAPA_STYLE: Record<EtapaDeal, string> = {
  interes: 'bg-soft text-ink/70',
  visita: 'bg-leaf/25 text-deep',
  propuesta: 'bg-leaf/45 text-deep',
  contratado: 'bg-brand/20 text-brand',
  contacto: 'bg-soft text-ink/70',
  documentacion: 'bg-leaf/25 text-deep',
  visita_fotos: 'bg-leaf/45 text-deep',
  alta: 'bg-brand/20 text-brand',
};
